import { useMemo, useState } from "preact/hooks";
import { route } from "preact-router";
import { LegalFooter } from "./footer";
import { Stepper } from "./stepper";
import { STRINGS } from "../constants/strings";
import { toErrorPath, toThankYouPath } from "../routes";
import type { ResendResponse } from "../types";

interface StoredData {
  pendingId: string;
  email?: string;
}

interface Props {
  token: string;
  storagePrefix: string;
  resend: (pendingId: string) => Promise<ResendResponse>;
  title: string;
  body: (email: string) => string;
  expiry: string;
}

type ResendState =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "sent" }
  | { kind: "rate_limited" }
  | { kind: "max_reached" }
  | { kind: "cooldown"; waitSeconds: number }
  | { kind: "error" };

function readStored(key: string): StoredData | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as StoredData;
  } catch {
    return null;
  }
}

export function PendingConfirmation({
  token,
  storagePrefix,
  resend,
  title,
  body,
  expiry,
}: Props) {
  const data = useMemo(
    () => readStored(`${storagePrefix}:${token}`),
    [storagePrefix, token]
  );
  const [resendState, setResendState] = useState<ResendState>({ kind: "idle" });

  const onResend = async () => {
    if (!data || resendState.kind === "sending") return;
    setResendState({ kind: "sending" });
    try {
      const res = await resend(data.pendingId);
      switch (res.status) {
        case "ok":
          setResendState({ kind: "sent" });
          return;
        case "already":
          route(toThankYouPath({}), true);
          return;
        case "rate_limited":
          setResendState({ kind: "rate_limited" });
          return;
        case "max_resends_reached":
          setResendState({ kind: "max_reached" });
          return;
        case "expired":
          route(toErrorPath("expired"), true);
          return;
        case "cooldown":
          setResendState({ kind: "cooldown", waitSeconds: res.waitSeconds });
          return;
      }
    } catch {
      setResendState({ kind: "error" });
    }
  };

  const email = data?.email || "Ihre E-Mail";

  return (
    <div class="tl-page">
      <div class="tl-page-centered">
        <div class="tl-icon-circle">
          <span class="material-icons" aria-hidden="true">
            mark_email_unread
          </span>
        </div>
        <h1 class="tl-screen-title">{title}</h1>
        <p class="tl-screen-body">{body(email)}</p>
        <Stepper
          steps={[
            { label: STRINGS.pendingStepSubmitted },
            { label: STRINGS.pendingStepConfirm },
            { label: STRINGS.pendingStepPublished },
          ]}
          activeIndex={1}
        />
        <div class="tl-amber-card">
          <span class="material-icons" aria-hidden="true">
            schedule
          </span>
          <span>{expiry}</span>
        </div>
        {data && (
          <div class="tl-link-row">
            {resendState.kind === "sending" ? (
              <span class="tl-link-row__pending">
                {STRINGS.pendingResendSending}
              </span>
            ) : (
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  onResend();
                }}
              >
                {STRINGS.pendingResend}
              </a>
            )}
          </div>
        )}
        {resendState.kind === "sent" && (
          <span class="tl-required-hint">{STRINGS.resendSuccess}</span>
        )}
        {resendState.kind === "rate_limited" && (
          <span class="tl-field__error" role="alert">
            {STRINGS.resendRateLimit}
          </span>
        )}
        {resendState.kind === "max_reached" && (
          <span class="tl-field__error" role="alert">
            {STRINGS.resendMaxReached}
          </span>
        )}
        {resendState.kind === "cooldown" && (
          <span class="tl-field__error" role="alert">
            {STRINGS.resendCooldown(resendState.waitSeconds)}
          </span>
        )}
        {resendState.kind === "error" && (
          <span class="tl-field__error" role="alert">
            {STRINGS.genericError}
          </span>
        )}
      </div>
      <LegalFooter />
    </div>
  );
}
